import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { signOut } from '../store/slices/authSlice'
import { colors } from '../constants/colors'
import Sidebar from '../components/(admin)/Sidebar'
import PageTopBar from '../components/(admin)/PageTopBar'
import TopQueriesChart from '../components/(admin)/charts/TopQueriesChart'
import ZeroHitTrendsChart from '../components/(admin)/charts/ZeroHitTrendsChart'
import BugReportsPieChart from '../components/(admin)/charts/BugReportsPieChart'
import { 
  fetchTopQueries,
  fetchZeroHitTrends,
  fetchBugReportsByStatus,
  fetchDashboardStats,
  fetchAnalyticsQueries,
  fetchAnalyticsZeroHits,
  fetchAnalyticsFlaggedBugs
} from '../store/slices/chartSlice'

const AnalyticsOverview = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { user, business } = useSelector((state) => state.auth)

  const [isSigningOut, setIsSigningOut] = useState(false)
  const [selectedTimeRange, setSelectedTimeRange] = useState('30')
  const tenantId = business?.id
  const [selectedTenant, setSelectedTenant] = useState(tenantId || 'all')

  // Get chart data from Redux
  const { 
    topQueries, 
    zeroHitTrends, 
    bugReportsByStatus: bugReports, 
    dashboardStats,
    analyticsQueries,
    analyticsZeroHits,
    analyticsFlaggedBugs,
    loading: isLoading 
  } = useSelector((state) => state.chart)

  // Fetch analytics data on component mount and when filters change
  useEffect(() => {
    dispatch(fetchTopQueries({ limit: 10, timeRange: selectedTimeRange, tenant: selectedTenant }))
    dispatch(fetchZeroHitTrends({ days: parseInt(selectedTimeRange), tenant: selectedTenant }))
    dispatch(fetchBugReportsByStatus({ timeRange: selectedTimeRange, tenant: selectedTenant }))
    dispatch(fetchDashboardStats({ timeRange: selectedTimeRange, tenant: selectedTenant }))
    dispatch(fetchAnalyticsQueries({ limit: 10, timeRange: selectedTimeRange, tenant: selectedTenant }))
    dispatch(fetchAnalyticsZeroHits({ limit: 10, timeRange: selectedTimeRange, tenant: selectedTenant }))
    dispatch(fetchAnalyticsFlaggedBugs({ limit: 10, timeRange: selectedTimeRange, tenant: selectedTenant }))
  }, [dispatch, selectedTimeRange, selectedTenant])

  // Keep selectedTenant in sync with logged-in tenant when business loads
  useEffect(() => {
    if (tenantId && selectedTenant !== tenantId) setSelectedTenant(tenantId)
  }, [tenantId])

  const handleSignOut = async () => {
    setIsSigningOut(true)
    
    try {
      await dispatch(signOut()).unwrap()
    } catch (error) {
      console.error('Logout failed:', error)
    } finally {
      setTimeout(() => {
        navigate('/signin')
        setIsSigningOut(false)
      }, 500)
    }
  }

  const containerStyle = {
    minHeight: '100vh',
    backgroundColor: colors.background.secondary,
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    display: 'flex'
  }

  const mainContentStyle = {
    flex: 1,
    marginLeft: '280px',
    padding: '24px',
    backgroundColor: colors.background.secondary
  }

  const headerStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '32px'
  }

  const headerLeftStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '16px'
  }

  const headerRightStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '16px'
  }

  const headerIconStyle = {
    width: '24px',
    height: '24px',
    cursor: 'pointer',
    color: colors.text.secondary
  }

  const notificationIconStyle = {
    ...headerIconStyle,
    position: 'relative'
  }

  const notificationDotStyle = {
    position: 'absolute',
    top: '-2px',
    right: '-2px',
    width: '8px',
    height: '8px',
    backgroundColor: '#ef4444',
    borderRadius: '50%'
  }

  const titleStyle = {
    fontSize: '28px',
    fontWeight: '700',
    color: colors.text.primary,
    margin: 0,
    marginBottom: '24px'
  }

  const filterContainerStyle = {
    display: 'flex',
    gap: '16px',
    marginBottom: '24px',
    alignItems: 'center'
  }

  const filterSelectStyle = {
    padding: '8px 12px',
    fontSize: '14px',
    border: `1px solid ${colors.borderVariants.light}`,
    borderRadius: '6px',
    backgroundColor: colors.background.primary,
    color: colors.text.primary,
    cursor: 'pointer'
  }

  const chartsGridStyle = {
    display: 'grid',
    gridTemplateColumns: '2fr 1fr',
    gap: '24px',
    marginBottom: '24px'
  }

  const chartCardStyle = {
    backgroundColor: colors.background.primary,
    padding: '24px',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
  }

  const chartTitleStyle = {
    fontSize: '18px',
    fontWeight: '600',
    color: colors.text.primary,
    margin: '0 0 20px 0'
  }

  const tablesGridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '24px',
    marginBottom: '24px'
  }

  const tableCardStyle = {
    backgroundColor: colors.background.primary,
    padding: '24px',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
  }

  const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse'
  }

  const tableHeaderStyle = {
    backgroundColor: colors.background.secondary,
    padding: '12px',
    textAlign: 'left',
    fontSize: '14px',
    fontWeight: '600',
    color: colors.text.primary,
    borderBottom: `1px solid ${colors.borderVariants.light}`
  }

  const tableCellStyle = {
    padding: '12px',
    fontSize: '14px',
    color: colors.text.primary,
    borderBottom: `1px solid ${colors.borderVariants.light}`
  }

  const loaderOverlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 9999
  }

  const loaderContainerStyle = {
    backgroundColor: colors.background.primary,
    padding: '40px',
    borderRadius: '12px',
    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.3)',
    textAlign: 'center',
    minWidth: '200px'
  }

  const spinnerStyle = {
    width: '40px',
    height: '40px',
    border: '4px solid #e2e8f0',
    borderTop: `4px solid ${colors.primary}`,
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    margin: '0 auto 20px auto'
  }

  const loaderTextStyle = {
    fontSize: '16px',
    color: colors.text.primary,
    margin: 0,
    fontWeight: '500'
  }

  // API data for tables (no static fallback)
  const queriesData = (analyticsQueries || []).map(item => ({
    query: item.query || item.text || item.question || 'Unknown Query',
    count: item.count || item.frequency || item.total || 0
  }))

  const zeroHitsData = (analyticsZeroHits || []).map(item => ({
    query: item.query || item.text || item.question || 'Unknown Query',
    count: item.count || item.zero_hits || item.value || 0
  }))

  const flaggedBugsData = (analyticsFlaggedBugs || []).map(item => ({
    query: item.query || item.text || item.title || 'Unknown Query',
    user: item.user || item.username || item.created_from || '—',
    date: item.date || (item.created_at ? new Date(item.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—')
  }))

  return (
    <div style={containerStyle}>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      
      {/* Sidebar */}
      <Sidebar />
      
      {/* Main Content */}
      <div style={mainContentStyle}>
        {/* Header */}
        <PageTopBar
          title="Analytics Overview"
          onAiConfig={() => navigate('/ai-configuration')}
          onEmbeddingGuide={() => navigate('/embedding-guide')}
          onSignOut={handleSignOut}
        />

        {/* Filter Controls */}
        <div style={filterContainerStyle}>
          <select 
            style={filterSelectStyle}
            value={selectedTimeRange}
            onChange={(e) => setSelectedTimeRange(e.target.value)}
          >
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
            <option value="365">Last year</option>
          </select>
          {tenantId && (
            <select 
              style={filterSelectStyle}
              value={selectedTenant}
              onChange={(e) => setSelectedTenant(e.target.value)}
            >
              <option value={tenantId}>Current tenant</option>
            </select>
          )}
        </div>

        {/* Charts Grid */}
        <div style={chartsGridStyle}>
          {/* Top Queries Chart */}
          <div style={chartCardStyle}>
            <h3 style={chartTitleStyle}>Top Queries</h3>
            <TopQueriesChart data={topQueries} isLoading={isLoading} />
          </div>

          {/* Zero Hit Trends Chart */}
          <div style={chartCardStyle}>
            <h3 style={chartTitleStyle}>Zero-Hits</h3>
            <ZeroHitTrendsChart data={zeroHitTrends} isLoading={isLoading} />
          </div>
        </div>

        {/* Tables Grid */}
        <div style={tablesGridStyle}>
          {/* Queries Table */}
          <div style={tableCardStyle}>
            <h3 style={chartTitleStyle}>Queries</h3>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={tableHeaderStyle}>Query</th>
                  <th style={tableHeaderStyle}>Count</th>
                </tr>
              </thead>
              <tbody>
                {queriesData.length === 0 ? (
                  <tr><td colSpan={2} style={{ ...tableCellStyle, textAlign: 'center', color: colors.text.secondary }}>No query data yet</td></tr>
                ) : (
                  queriesData.map((item, index) => (
                    <tr key={index}>
                      <td style={tableCellStyle}>{item.query}</td>
                      <td style={tableCellStyle}>{item.count}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Zero Hits Table */}
          <div style={tableCardStyle}>
            <h3 style={chartTitleStyle}>Zero Hits</h3>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={tableHeaderStyle}>Queries</th>
                  <th style={tableHeaderStyle}>O-Hits</th>
                </tr>
              </thead>
              <tbody>
                {zeroHitsData.length === 0 ? (
                  <tr><td colSpan={2} style={{ ...tableCellStyle, textAlign: 'center', color: colors.text.secondary }}>No zero-hit data yet</td></tr>
                ) : (
                  zeroHitsData.map((item, index) => (
                    <tr key={index}>
                      <td style={tableCellStyle}>{item.query}</td>
                      <td style={tableCellStyle}>{item.count}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Flagged Bugs Table */}
          <div style={tableCardStyle}>
            <h3 style={chartTitleStyle}>Flagged Bugs</h3>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={tableHeaderStyle}>Query</th>
                  <th style={tableHeaderStyle}>User</th>
                  <th style={tableHeaderStyle}>Date</th>
                </tr>
              </thead>
              <tbody>
                {flaggedBugsData.length === 0 ? (
                  <tr><td colSpan={3} style={{ ...tableCellStyle, textAlign: 'center', color: colors.text.secondary }}>No flagged bugs yet</td></tr>
                ) : (
                  flaggedBugsData.map((item, index) => (
                    <tr key={index}>
                      <td style={tableCellStyle}>{item.query}</td>
                      <td style={tableCellStyle}>{item.user}</td>
                      <td style={tableCellStyle}>{item.date}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Sign Out Loader Overlay */}
      {isSigningOut && (
        <div style={loaderOverlayStyle}>
          <div style={loaderContainerStyle}>
            <div style={spinnerStyle}></div>
            <p style={loaderTextStyle}>Signing out...</p>
          </div>
        </div>
      )}
    </div>
  )
}

export default AnalyticsOverview
